import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './NewArrivalsPage.css';
import ProductCard from '../common/ProductCard';
import * as ProductService from '../../services/ProductService';
import { DIRECT_PRODUCTS, searchProducts } from '../../data/ProductData';
import { CircularProgress, Box } from '@mui/material';
import { Search } from 'lucide-react';
import { Helmet } from 'react-helmet';

const NewArrivalsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchNewArrivals = async () => {
      setLoading(true);
      try {
        // Use direct products instead of API
        const newArrivals = [...DIRECT_PRODUCTS]
          .filter(p => p.inStock)
          // Sort by ID in descending order (assuming higher IDs are newer products)
          // This ensures products with the latest IDs appear at the top
          .sort((a, b) => {
            // First try to sort by numeric ID if the format is 'direct123'
            const aIdNum = a.id.replace(/\D/g, '');
            const bIdNum = b.id.replace(/\D/g, '');
            
            if (aIdNum && bIdNum) {
              return parseInt(bIdNum) - parseInt(aIdNum);
            }
            
            // Fallback to creation date if IDs don't have numeric components
            return new Date(b.createdAt) - new Date(a.createdAt);
          });
        
        setProducts(newArrivals);
        setFilteredProducts(newArrivals);
      } catch (error) {
        console.error('Error fetching new arrivals:', error);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchNewArrivals();
  }, []);

  // Filter products when category or search term changes
  useEffect(() => {
    let filtered = products;
    
    // First filter by search term if it exists
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(term) ||
        (product.description && product.description.toLowerCase().includes(term))
      );
    }
    
    // Then filter by category if not 'all'
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(
        product => product.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    }
    
    setFilteredProducts(filtered);
  }, [selectedCategory, products, searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Create structured data for new arrivals
  const newArrivalsStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": filteredProducts.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.name,
        "image": product.image.startsWith('http') 
          ? product.image 
          : `https://nmdiamond.in${product.image}`,
        "description": product.description || `${product.name} from NM Diamond`,
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "INR",
          "availability": product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "offerCount": 1
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.5",
          "reviewCount": "10"
        }
      }
    }))
  };

  return (
    <section className="collection-page new-arrivals-page">
      <Helmet>
        <title>New Arrivals | NM Diamond</title>
        <meta name="description" content="Discover our latest diamond jewelry arrivals including rings, necklaces, earrings, and bracelets." />
        <script type="application/ld+json">
          {JSON.stringify(newArrivalsStructuredData)}
        </script>
      </Helmet>
      <div className="container mt-5">
        <div className="new-arrivals-header">
          <h2 className="section-title">New Arrivals</h2>
          <p className="section-subtitle">Discover our latest jewelry pieces</p>
        </div>

        <div className="filters">
          <div className="search-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
              <Search size={20} className="search-icon" />
            </div>
          </div>
          
          <div className="category-filters">
            <button
              className={selectedCategory === 'all' ? 'active' : ''}
              onClick={() => setSelectedCategory('all')}
            >
              All
            </button>
            <button
              className={selectedCategory === 'rings' ? 'active' : ''}
              onClick={() => setSelectedCategory('rings')}
            >
              Rings
            </button>
            <button
              className={selectedCategory === 'necklaces' ? 'active' : ''}
              onClick={() => setSelectedCategory('necklaces')}
            >
              Necklaces
            </button>
            <button
              className={selectedCategory === 'earrings' ? 'active' : ''}
              onClick={() => setSelectedCategory('earrings')}
            >
              Earrings
            </button>
            <button
              className={selectedCategory === 'bracelets' ? 'active' : ''}
              onClick={() => setSelectedCategory('bracelets')}
            >
              Bracelets
            </button>
          </div>
        </div>

        <div className="product-grid">
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : filteredProducts.length === 0 ? (
            <div className="no-products-message">
              {searchTerm ? 
                `No products found matching "${searchTerm}" in this category. Try a different search term or category.` :
                'No products found in this category. Check back soon!'
              }
            </div>
          ) : (
            filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default NewArrivalsPage;